import { IconArchive, IconArchiveOff, IconCopy, IconEdit, IconEye, IconFileSpreadsheet, IconFileTypePdf, IconShare, IconTrash } from "@tabler/icons-react"
import {
  Box,
  type ButtonProps,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr
} from "@chakra-ui/react"
import React from "react"
import { formatDate } from "../../../common/commonConstants"
import { Colors } from "../../../theme/colors"
import { type FmpProject } from "../../../type-and-hooks"
import { ArchiveOrRestoreFMP } from "../common.fmp.actions/fmp.archive.main"
import { CopyFMP } from "../common.fmp.actions/fmp.copyfmp.main"
import { DeleteFMP } from "../common.fmp.actions/fmp.deletefmp.main"
import { ShareFMP } from "./shareFMP"
import { type IconKeyValuePairsInterface } from "./myFMPs.support.tableHeaders"

const ICON_BTN_SIZE = "32px"
const ICON_SIZE = 22
const SHOW_SHARE_ICON = false
// Project Name, Project Address, Contractor Name, Contractor Address, Rev, Created, Modified, Actions
const TOTAL_COLUMNS = 8

// Design tokens: this codebase styles via Chakra props + the shared Colors
// object rather than raw CSS custom properties, so the --text-secondary /
// --surface-1 / --text-primary / --bg-danger / --text-danger / --radius
// tokens from the spec are mapped to their closest existing equivalents here.
const iconColor = "#4A4A4A"
const textPrimary = Colors.Secondary.c2
const surface1 = Colors.Surface.s1
const bgDanger = Colors.Errors[100]
const textDanger = Colors.Errors.error
const radius = "6px"

const iconButtonStyle = (isDanger?: boolean): ButtonProps => ({
  variant: "unstyled",
  minW: ICON_BTN_SIZE,
  w: ICON_BTN_SIZE,
  h: ICON_BTN_SIZE,
  minH: ICON_BTN_SIZE,
  borderRadius: radius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bg: "transparent",
  color: iconColor,
  _hover: isDanger ? { bg: bgDanger, color: textDanger } : { bg: surface1, color: textPrimary }
})

interface IconActionButtonProps {
  label: string
  icon: React.ElementType
  onClick?: () => void
  isLoading?: boolean
  isDisabled?: boolean
  isDanger?: boolean
}

const IconActionButton = ({ label, icon: Icon, onClick, isLoading, isDisabled, isDanger }: IconActionButtonProps): JSX.Element => (
  <Tooltip label={label} hasArrow fontSize="11px" placement="top">
    <IconButton
      aria-label={label}
      icon={<Icon size={ICON_SIZE} stroke={1.75} />}
      onClick={onClick}
      isLoading={isLoading}
      isDisabled={isDisabled}
      {...iconButtonStyle(isDanger)}
    />
  </Tooltip>
)

const ActionDivider = (): JSX.Element => <Box w="0.5px" h="18px" bg={Colors.SecondaryPrimary[300]} mx="6px" flexShrink={0} />

// Grey dividers between columns and rows, matching the row/column borders
// used elsewhere in the table.
const BORDER_RIGHT_COLOR = Colors.Secondary.c2_500

// Project Name, Project Address, Contractor Name, Contractor Address, Rev,
// Created, Modified, Actions. Percentage widths (summing to 100%) so the
// table fills the page container instead of forcing horizontal scroll.
// table-layout stays "auto" (see MyFMPCompactTable) so these are treated as
// proportional hints - the Actions column's unbreakable icon row still gets
// widened past its share if it needs to, rather than wrapping.
export const COLUMN_WIDTHS = ["15%", "20%", "12%", "18%", "4%", "8%", "8%", "15%"]

export const MyFMPTableColGroup = (): JSX.Element => (
  <colgroup>
    {COLUMN_WIDTHS.map((width, index) => (
      <col key={index} style={{ width }} />
    ))}
  </colgroup>
)

// Matches the column heading style used on the edit-fmp page table
// (fmpProjectTable.tsx): coloured background, white bold PT Sans text.
// bg is overridable (e.g. grey for the archived FMPs table) while
// everything else stays identical between tables.
export const getHeaderCellProps = (bg: string): Record<string, unknown> => ({
  bg,
  color: "white",
  fontFamily: "PT Sans",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "16px",
  textTransform: "none" as const,
  letterSpacing: "normal",
  p: "8px"
})

// Shared with the empty-state placeholder so the column headers look
// identical whether or not there's any data to show.
export const MyFMPTableHeaderRow = ({ headerBg }: { headerBg?: string }): JSX.Element => {
  const headerCellProps = getHeaderCellProps(headerBg ?? Colors.Alternative.accent)

  return (
    <Thead>
      <Tr>
        <Th {...headerCellProps} textAlign="left" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Project Name
        </Th>
        <Th {...headerCellProps} textAlign="left" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Project Address
        </Th>
        <Th {...headerCellProps} textAlign="left" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Contractor Name
        </Th>
        <Th {...headerCellProps} textAlign="left" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Contractor Address
        </Th>
        <Th {...headerCellProps} textAlign="center" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Rev
        </Th>
        <Th {...headerCellProps} textAlign="center" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Created
        </Th>
        <Th {...headerCellProps} textAlign="center" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Modified
        </Th>
        <Th {...headerCellProps} textAlign="left" borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}>
          Actions
        </Th>
      </Tr>
    </Thead>
  )
}

interface MyFMPCompactTableProps {
  tableData: Array<FmpProject | null> | null | undefined
  handleClick: (item: IconKeyValuePairsInterface["label"], id: number | null | undefined, projectName?: string | null | undefined) => void
  onPdfOptionClick: (id: number | null | undefined, option: number) => void
  refetchData: VoidFunction
  getShareUrl: (fmpId: string | undefined | number) => string | undefined
  PDFDownloadLoading: boolean
  fmpSelectedForPDF: number | null
  excelDownloadingId: number | null
  excelDownloadLoading: boolean
  headerBg?: string
  isArchived?: boolean
}

export const MyFMPCompactTable = ({
  tableData,
  handleClick,
  onPdfOptionClick,
  refetchData,
  getShareUrl,
  PDFDownloadLoading,
  fmpSelectedForPDF,
  excelDownloadingId,
  excelDownloadLoading,
  headerBg,
  isArchived = false
}: MyFMPCompactTableProps): JSX.Element => (
  <Box w="100%" overflow="auto">
    <Table w="100%">
      <MyFMPTableColGroup />
      <MyFMPTableHeaderRow headerBg={headerBg} />
      <Tbody>
        {tableData?.map((fmpRow: FmpProject | null, rowIndex) => {
          const isPdfLoadingForRow = PDFDownloadLoading && fmpSelectedForPDF === fmpRow?.id
          const isExcelLoadingForRow = excelDownloadingId === fmpRow?.id && excelDownloadLoading

          return (
            <Tr key={`CompactRow${rowIndex}`} _hover={{ bg: surface1 }}>
              <Td
                color={Colors.Primary.c1}
                fontWeight="500"
                fontSize="14px"
                wordBreak="break-word"
                whiteSpace="normal"
                textAlign="left"
                px="12px"
                borderLeft={`1px solid ${Colors.Secondary.c2_500}`}
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {fmpRow?.project_name ?? "-"}
              </Td>
              <Td
                fontSize="13px"
                wordBreak="break-word"
                whiteSpace="normal"
                textAlign="left"
                px="12px"
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {fmpRow?.project_address ?? "-"}
              </Td>
              <Td
                fontSize="13px"
                wordBreak="break-word"
                whiteSpace="normal"
                textAlign="left"
                px="12px"
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {fmpRow?.subcontractor_name ?? "-"}
              </Td>
              <Td
                fontSize="13px"
                wordBreak="break-word"
                whiteSpace="normal"
                textAlign="left"
                px="12px"
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {fmpRow?.subcontractor_address ?? "-"}
              </Td>
              <Td
                fontSize="13px"
                textAlign="center"
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {fmpRow?.revision ?? "-"}
              </Td>
              <Td
                fontSize="13px"
                whiteSpace="nowrap"
                textAlign="center"
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {formatDate(fmpRow?.date_created ?? "") || "-"}
              </Td>
              <Td
                fontSize="13px"
                whiteSpace="nowrap"
                textAlign="center"
                borderRight={`1px solid ${BORDER_RIGHT_COLOR}`}
                borderBottom={`1px solid ${Colors.Secondary.c2_500}`}
              >
                {formatDate(fmpRow?.date_modified ?? "") || "-"}
              </Td>
              <Td borderRight={`1px solid ${BORDER_RIGHT_COLOR}`} borderBottom={`1px solid ${Colors.Secondary.c2_500}`}>
                <Box display="flex" alignItems="center" justifyContent="flex-start">
                  <IconActionButton label="View" icon={IconEye} onClick={() => { handleClick("View", fmpRow?.id) }} />
                  <IconActionButton label="Edit" icon={IconEdit} onClick={() => { handleClick("Edit", fmpRow?.id) }} />
                  <Tooltip label="Copy" hasArrow fontSize="11px" placement="top">
                    <Box as="span" display="inline-block">
                      <CopyFMP
                        fmpId={fmpRow?.id}
                        projectName={fmpRow?.project_name}
                        onCopySuccess={refetchData}
                        buttonProps={iconButtonStyle()}
                      >
                        <IconCopy size={ICON_SIZE} stroke={1.75} />
                      </CopyFMP>
                    </Box>
                  </Tooltip>

                  <ActionDivider />

                  <Tooltip label="PDF Download" hasArrow fontSize="11px" placement="top">
                    <Box as="span" display="inline-block">
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label="PDF Download"
                          icon={<IconFileTypePdf size={ICON_SIZE} stroke={1.75} />}
                          isLoading={isPdfLoadingForRow}
                          isDisabled={isPdfLoadingForRow}
                          {...iconButtonStyle()}
                        />
                        <MenuList px="2" borderColor="primary" borderWidth="1px" borderRadius="0" minW="140px">
                          <MenuItem
                            justifyContent="center"
                            borderBottom="1px"
                            borderColor="primary"
                            color="primary"
                            _hover={{ bg: "primary", color: "white" }}
                            onClick={() => { onPdfOptionClick(fmpRow?.id, 0) }}
                            fontSize="14px"
                          >
                            FMP
                          </MenuItem>
                          <MenuItem
                            justifyContent="center"
                            borderBottom="1px"
                            borderColor="primary"
                            color="primary"
                            _hover={{ bg: "primary", color: "white" }}
                            onClick={() => { onPdfOptionClick(fmpRow?.id, 1) }}
                            fontSize="14px"
                          >
                            FMP + Reports
                          </MenuItem>
                          <MenuItem
                            justifyContent="center"
                            color="primary"
                            _hover={{ bg: "primary", color: "white" }}
                            onClick={() => { onPdfOptionClick(fmpRow?.id, 2) }}
                            fontSize="14px"
                          >
                            Reports Only
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Box>
                  </Tooltip>
                  <IconActionButton
                    label="Excel Report"
                    icon={IconFileSpreadsheet}
                    onClick={() => { handleClick("Excel Report", fmpRow?.id) }}
                    isLoading={isExcelLoadingForRow}
                  />
                  {SHOW_SHARE_ICON && fmpRow?.id !== null && fmpRow?.id !== undefined && (
                    <Tooltip label="Share" hasArrow fontSize="11px" placement="top">
                      <Box as="span" display="inline-block">
                        <ShareFMP
                          fmpId={Number(fmpRow?.id)}
                          projectName={fmpRow?.project_name ?? ""}
                          buttonStyles={iconButtonStyle()}
                          shareUrl={getShareUrl(fmpRow?.id ?? "")}
                        >
                          <IconShare size={ICON_SIZE} stroke={1.75} />
                        </ShareFMP>
                      </Box>
                    </Tooltip>
                  )}

                  <ActionDivider />

                  <Tooltip label={isArchived ? "Unarchive" : "Archive"} hasArrow fontSize="11px" placement="top">
                    <Box as="span" display="inline-block">
                      <ArchiveOrRestoreFMP
                        fmpName={fmpRow?.project_name ?? null}
                        fmpId={fmpRow?.id}
                        onArchiveSuccess={refetchData}
                        actionType={isArchived ? "restore" : "archive"}
                        buttonProps={iconButtonStyle()}
                      >
                        {isArchived ? <IconArchiveOff size={ICON_SIZE} stroke={1.75} /> : <IconArchive size={ICON_SIZE} stroke={1.75} />}
                      </ArchiveOrRestoreFMP>
                    </Box>
                  </Tooltip>
                  <Tooltip label="Delete" hasArrow fontSize="11px" placement="top">
                    <Box as="span" display="inline-block">
                      <DeleteFMP onDeleteSuccess={refetchData} fmpId={fmpRow?.id} deleteButtonProps={iconButtonStyle(true)}>
                        <IconTrash size={ICON_SIZE} stroke={1.75} />
                      </DeleteFMP>
                    </Box>
                  </Tooltip>
                </Box>
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  </Box>
)
